// Full inventory panel — tabbed (Equipment / Consumables / Quest / Materials / Misc).
// Item icons via asset_loader. Click to equip/unequip/drop.
// Opened by 'I' key or HUD button.

import type { Net } from "../net.js";
import type { ClientStore, ClientState } from "../state.js";
import type { OwnedItemView } from "../../shared/protocol.js";
import { assetUrl } from "../asset_loader.js";

type Tab = "equipment" | "consumables" | "quest" | "materials" | "misc";

const TAB_LABELS: Record<Tab, string> = {
  equipment: "Equipment",
  consumables: "Consumables",
  quest: "Quest",
  materials: "Materials",
  misc: "Misc",
};

const TAB_ORDER: Tab[] = ["equipment", "consumables", "quest", "materials", "misc"];

function categorise(item: OwnedItemView): Tab {
  const c = (item.category || "").toLowerCase();
  if (c === "weapon" || c === "armor" || c === "bag") return "equipment";
  if (c === "consumable" || c === "ammo") return "consumables";
  if (c === "quest") return "quest";
  if (c === "material") return "materials";
  return "misc";
}

const QUALITY_COLOR: Record<string, string> = {
  Common:    "#c5c5c5",
  Uncommon:  "#4aa84a",
  Rare:      "#4a8cff",
  Epic:      "#b366ff",
  Legendary: "#ff8c1a",
  None:      "#888",
};

export function openInventory(host: HTMLElement, net: Net, store: ClientStore): HTMLElement {
  const existing = host.querySelector<HTMLElement>(".inv-overlay");
  if (existing) { existing.remove(); return existing; }

  const overlay = document.createElement("div");
  overlay.className = "inv-overlay";
  host.appendChild(overlay);

  // Refresh inventory on open
  net.send({ t: "inventory" });

  let activeTab: Tab = "equipment";

  function render(s: ClientState): void {
    const bucketed: Record<Tab, OwnedItemView[]> = {
      equipment: [], consumables: [], quest: [], materials: [], misc: [],
    };
    for (const item of s.inventory) bucketed[categorise(item)].push(item);

    overlay.innerHTML = `
      <div class="inv-modal" role="dialog" aria-label="Inventory">
        <header class="inv-header">
          <h2>Inventory</h2>
          <span class="inv-count dim">${s.inventory.length} items</span>
          <button id="inv-close" class="inv-close" aria-label="Close">×</button>
        </header>
        <nav class="inv-tabs">
          ${TAB_ORDER.map((tab) => `
            <button class="inv-tab ${tab === activeTab ? "active" : ""}" data-tab="${tab}">
              ${TAB_LABELS[tab]} <span class="dim">${bucketed[tab].length}</span>
            </button>
          `).join("")}
        </nav>
        <section class="inv-grid">
          ${bucketed[activeTab].length === 0
            ? `<p class="dim inv-empty">Nothing in ${TAB_LABELS[activeTab].toLowerCase()}.</p>`
            : bucketed[activeTab].map(renderSlot).join("")}
        </section>
      </div>
    `;

    // Tab switching
    for (const b of overlay.querySelectorAll<HTMLButtonElement>("button.inv-tab")) {
      b.addEventListener("click", () => {
        activeTab = b.dataset.tab as Tab;
        render(store.get());
      });
    }
    // Action buttons
    for (const b of overlay.querySelectorAll<HTMLButtonElement>("[data-equip]")) {
      b.addEventListener("click", () => net.send({ t: "equip", instance_id: Number(b.dataset.equip) }));
    }
    for (const b of overlay.querySelectorAll<HTMLButtonElement>("[data-unequip]")) {
      b.addEventListener("click", () => net.send({ t: "unequip", slot: b.dataset.unequip! }));
    }
    for (const b of overlay.querySelectorAll<HTMLButtonElement>("[data-drop]")) {
      b.addEventListener("click", () => net.send({ t: "drop", instance_id: Number(b.dataset.drop) }));
    }
    overlay.querySelector<HTMLButtonElement>("#inv-close")?.addEventListener("click", () => cleanup());
  }

  function renderSlot(i: OwnedItemView): string {
    const borderColor = QUALITY_COLOR[i.quality ?? "None"] ?? "#888";
    const icon = assetUrl("icon", i.template_id, { name: i.name, category: i.category });
    const equipBtn = i.slot && !i.equipped_slot
      ? `<button class="inv-action" data-equip="${i.id}">equip</button>` : "";
    const unequipBtn = i.equipped_slot
      ? `<button class="inv-action" data-unequip="${escape(i.equipped_slot)}">unequip</button>` : "";
    const dropBtn = i.category !== "Quest"
      ? `<button class="inv-action" data-drop="${i.id}">drop</button>` : "";
    return `
      <div class="inv-slot" style="border-color:${borderColor}" title="${escape(tooltipFor(i))}">
        <img class="inv-icon" src="${escape(icon)}" alt="" />
        <div class="inv-slot-name">${escape(i.name)}</div>
        <div class="inv-slot-meta dim">
          ${escape(i.sub_type ?? i.category)}${i.qty > 1 ? " ×" + i.qty : ""}
          ${i.equipped_slot ? ` · <strong>${escape(i.equipped_slot)}</strong>` : ""}
        </div>
        <div class="inv-actions">${equipBtn} ${unequipBtn} ${dropBtn}</div>
      </div>
    `;
  }

  function tooltipFor(i: OwnedItemView): string {
    const parts: string[] = [i.name];
    if (i.quality && i.quality !== "None") parts.push(`[${i.quality}]`);
    if (i.category) parts.push(i.category + (i.sub_type ? ` — ${i.sub_type}` : ""));
    if (i.slot) parts.push(`slot: ${i.slot}`);
    if (i.binding && i.binding !== "None") parts.push(`binding: ${i.binding}`);
    if (i.stack_limit && i.stack_limit > 1) parts.push(`stacks to ${i.stack_limit}`);
    return parts.join(" · ");
  }

  let cleanup: () => void = () => { /* set below */ };
  const unsubscribe = store.subscribe(render);
  render(store.get());
  cleanup = (): void => {
    unsubscribe();
    document.removeEventListener("keydown", onEsc);
    overlay.remove();
    store.closePanel();
  };
  function onEsc(ev: KeyboardEvent): void { if (ev.key === "Escape") cleanup(); }
  (overlay as unknown as { __cleanup: () => void }).__cleanup = cleanup;
  overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(); });
  document.addEventListener("keydown", onEsc);
  return overlay;
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
